import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Box, Container, Typography, Card, CardContent, Paper, Avatar, CircularProgress } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import Chart from 'react-apexcharts';
import { Upload, CheckCircle, HourglassEmpty, People, Visibility, GetApp, Person, Assignment, PersonAdd } from '@mui/icons-material';
import { primaryColor, secondaryColor } from '../theme';
import moment from 'moment';
import DataTable from 'react-data-table-component';
import { Link } from 'react-router-dom';

const RegionalCoordinatorDashboard = () => {
  const theme = useTheme();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userId, setUserId] = useState('');

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const userId = JSON.parse(localStorage.getItem('user')).id;
        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/regional-dashboard`, {
          params: { userId }
        });
        setDashboardData(response.data);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <CircularProgress />
      </Container>
    );
  }

  if (!dashboardData) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Typography variant="h6" color="error">Dashboard data not found</Typography>
      </Container>
    );
  }

  const { recentActivity, viewsDownloadsData, contentData, userCount } = dashboardData;

  const chartOptions = {
    chart: {
      type: 'area',
      height: 350,
      zoom: {
        enabled: false
      }
    },
    dataLabels: {
      enabled: false
    },
    stroke: {
      curve: 'smooth'
    },
    title: {
      text: 'Content Views and Downloads Trend',
      align: 'left',
      style: {
        color: primaryColor, // Use the primary color for the title
        fontSize: '24px',
        fontWeight: 'bold'
      }
    },
    xaxis: {
      categories: viewsDownloadsData.map(item => item.date)
    },
    tooltip: {
      x: {
        format: 'dd/MM/yy HH:mm'
      },
    },
    colors: [theme.palette.primary.main, theme.palette.secondary.main]
  };

  const chartSeries = [
    {
      name: 'Views',
      data: viewsDownloadsData.map(item => item.views)
    },
    {
      name: 'Downloads',
      data: viewsDownloadsData.map(item => item.downloads)
    }
  ];

  const indexedContentData = contentData.map((item, index) => ({ ...item, index: index + 1 }));

  const columns = [
    { name: 'No.', selector: row => row.index, sortable: true, width: '80px' }, 
    { name: 'Topic', selector: row => row.topic, sortable: true },
    { name: 'Value Chain', selector: row => row.valueChain, sortable: true },
    { name: 'Platform', selector: row => row.platform, sortable: true },
    { name: 'Target User', selector: row => row.targetUser, sortable: true },
    { name: 'Date Uploaded', selector: row => row.dateUploaded, sortable: true },
    { name: 'Date Approved', selector: row => row.dateApproved, sortable: true },
    { name: 'Views', selector: row => row.views, sortable: true },
    { name: 'Downloads', selector: row => row.downloads, sortable: true },
  ];

  const getTimeAgo = (date) => {
    return moment(date).fromNow();
  };

  const getActivityIcon = (type) => {
    switch (type) {
      case 'content':
        return <Assignment />;
      case 'user':
        return <PersonAdd />;
      default:
        return <Person />;
    }
  };

  const user = JSON.parse(localStorage.getItem('user')) || { firstName: 'User', role: 'Role' };

  return (
    <Container maxWidth="false" sx={{ mt: 4, display: 'flex' }}>
      <Box sx={{ width: { xs: '100%', md: '80%' } }}>
        <Box sx={{ width: '100%', mb: 2 }}>
          <Typography
            variant="h4"
            gutterBottom
            sx={{
              color: primaryColor,
              backgroundColor: '#f5f5f5',
              padding: '16px',
              borderRadius: '8px',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
            }}
          >
            Hello, <span style={{ fontSize: '24px' }}>Welcome to <span style={{ fontWeight: 'bold' }}>{user.location}, {user.role}</span> Dashboard!</span>
          </Typography>
        </Box>
        <Box display="flex" flexWrap="wrap" mb={4}>
          <Box sx={{ flex: '1 1 33%', p: 1 }}>
            <Card>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" p={2} sx={{ borderRadius: '8px' }}>
                  <Box>
                    <Typography variant="h6" sx={{ color: primaryColor }}>Content Uploaded</Typography>
                    <Typography variant="h4" sx={{ color: secondaryColor, fontWeight: 'bold' }}>{contentData.length}</Typography>
                  </Box>
                  <Upload fontSize="large" sx={{ color: primaryColor }} />
                </Box>
              </CardContent>
            </Card>
          </Box>
          <Box sx={{ flex: '1 1 33%', p: 1 }}>
            <Card>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" p={2} sx={{ borderRadius: '8px' }}>
                  <Box>
                    <Typography variant="h6" sx={{ color: primaryColor }}>Approved Content</Typography>
                    <Typography variant="h4" sx={{ color: secondaryColor, fontWeight: 'bold' }}>{contentData.filter(content => content.status === 'Approved').length}</Typography>
                  </Box>
                  <CheckCircle fontSize="large" sx={{ color: primaryColor }} />
                </Box>
              </CardContent>
            </Card>
          </Box>
          <Box sx={{ flex: '1 1 33%', p: 1 }}>
            <Card>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" p={2} sx={{ borderRadius: '8px' }}>
                  <Box>
                    <Typography variant="h6" sx={{ color: primaryColor }}>Pending Content</Typography>
                    <Typography variant="h4" sx={{ color: secondaryColor, fontWeight: 'bold' }}>{contentData.filter(content => content.status === 'Pending Approval').length}</Typography>
                  </Box>
                  <HourglassEmpty fontSize="large" sx={{ color: primaryColor }} />
                </Box>
              </CardContent>
            </Card>
          </Box>
          <Box sx={{ flex: '1 1 33%', p: 1 }}>
            <Card>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" p={2} sx={{ borderRadius: '8px' }}>
                  <Box>
                    <Typography variant="h6" sx={{ color: primaryColor }}>Number of Trainers</Typography>
                    <Typography variant="h4" sx={{ color: secondaryColor, fontWeight: 'bold' }}>{userCount}</Typography>
                  </Box>
                  <People fontSize="large" sx={{ color: primaryColor }} />
                </Box>
              </CardContent>
            </Card>
          </Box>
          <Box sx={{ flex: '1 1 33%', p: 1 }}>
            <Card>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" p={2} sx={{ borderRadius: '8px' }}>
                  <Box>
                    <Typography variant="h6" sx={{ color: primaryColor }}>Content Views</Typography>
                    <Typography variant="h4" sx={{ color: secondaryColor, fontWeight: 'bold' }}>{contentData.reduce((total, content) => total + content.views, 0)}</Typography>
                  </Box>
                  <Visibility fontSize="large" sx={{ color: primaryColor }} />
                </Box>
              </CardContent>
            </Card>
          </Box>
          <Box sx={{ flex: '1 1 33%', p: 1 }}>
            <Card>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" p={2} sx={{ borderRadius: '8px' }}>
                  <Box>
                    <Typography variant="h6" sx={{ color: primaryColor }}>Downloads</Typography>
                    <Typography variant="h4" sx={{ color: secondaryColor, fontWeight: 'bold' }}>0</Typography>
                  </Box>
                  <GetApp fontSize="large" sx={{ color: primaryColor }} />
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Box>
        <Box sx={{ mb: 4 }}>
          <Chart options={chartOptions} series={chartSeries} type="area" height={350} />
        </Box>

        <Typography variant="h5" gutterBottom style={{ color: primaryColor, marginTop: '2rem' }}>
          Uploaded Content
        </Typography>
        <Box sx={{ width: '100%' }}>
          <DataTable
            columns={columns}
            data={indexedContentData}
            pagination
            highlightOnHover
            pointerOnHover
            customStyles={{
              headCells: {
                style: {
                  backgroundColor: primaryColor,
                  color: '#fff',
                  fontSize: '16px',
                  fontWeight: 'bold',
                },
              },
              cells: {
                style: {
                  borderBottom: '1px solid rgba(224, 224, 224, 1)',
                },
              },
            }}
          />
        </Box>
      </Box>

      <Box sx={{ display: { xs: 'none', md: 'block' }, width: '20%', ml: 4 }}>
        <Typography variant="h5" gutterBottom style={{ color: primaryColor }}>
          Recent Activity
        </Typography>
        <Box sx={{ position: 'relative' }}>
          {recentActivity.map((activity, index) => (
            <Box key={activity.id} display="flex" alignItems="center" mb={2} position="relative">
              <Avatar sx={{ bgcolor: primaryColor, mr: 2 }}>
                {getActivityIcon(activity.type)}
              </Avatar>
              <Box>
                <Box display="flex" alignItems="center" mb={1} gap={1}>
                  <Typography variant="body1">{activity.User.firstName} ({activity.User.role})</Typography>
                  <Typography variant="caption" color="textSecondary">{getTimeAgo(activity.date)}</Typography>
                </Box>
                <Typography variant="body2">{activity.activity}</Typography>
              </Box>
              {index < recentActivity.length - 1 && (
                <Box
                  sx={{
                    position: 'absolute',
                    left: '20px',
                    top: 'calc(60% + 20px)', // Adjust the top position to start below the icon
                    bottom: '-20px',
                    width: '2px',
                    bgcolor: primaryColor,
                  }}
                />
              )}
            </Box>
          ))}
          <Link to="/logs" style={{ textDecoration: 'none', color: primaryColor }}>
            <Typography variant="body2" sx={{ mt: 2, textAlign: 'center' }}>
              View More
            </Typography>
          </Link>
        </Box>
      </Box>
    </Container>
  );
};

export default RegionalCoordinatorDashboard;